import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { ProductApi } from '../../../api/productApi';
import type { SelectOption } from './common/Select';
import Select from './common/Select';
import { MultipleSelect } from './common/MultipleSelect';
import { CloseIcon } from '../../../assets/icons/CloseIcon';

const Container = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 4px;
    width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    overflow-x: auto;
  }
`;

const DesktopContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const DropdownsContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  padding-right: 8px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding-left: 16px;
  border-left: 1px solid #dbdbdb;
`;

const MobileTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 14px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1c1c1c;
  border-radius: 12px;
  border: 1px solid #e2e2e2;
  padding: 10px 12px;
  gap: 10px;
  color: var(--Text-on-Dark-BG-10, #dcdcdc);
  text-align: center;
  font-feature-settings: 'ss10' on;
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.4;
  transition: background 0.15s;
  cursor: default;

  &:hover {
    background: #313131;
  }
`;

const TagClose = styled.button`
  background: none;
  border: none;
  color: #b0b0b8;
  font-size: 14px;
  margin-left: 4px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  vertical-align: middle;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: none;
    color: #3d1690;
  }
`;

export const sortOptions: SelectOption[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'highest', label: 'Highest Rating' },
  { value: 'lowest', label: 'Lowest Rating' },
];

interface ReviewFilterBarProps {
  onSortChange: (value: string) => void;
  onSkinTypeChange: (value: string[]) => void;
  onSkinConcernChange: (value: string[]) => void;
  sortBy?: string;
  skinType?: string[];
  skinConcern?: string[];
}

export const ReviewFilterBar = ({ onSortChange, onSkinTypeChange, onSkinConcernChange }: ReviewFilterBarProps) => {
  const [sort, setSort] = useState<string>('');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // taxonomy 데이터 fetch (모달이 열릴 때만)
  const { data: taxonomy } = useQuery({
    queryKey: ['taxonomy'],
    queryFn: ProductApi.getTaxonomy,
  });

  // 옵션 변환
  const skinTypeOptions: SelectOption[] =
    taxonomy?.skinType?.map((item) => ({
      label: item.name.split(' ')?.at(0) || '',
      value: item.name.split(' ')?.at(0) || '', // @TODO: API has problem
    })) || [];
  const skinConcernOptions: SelectOption[] =
    taxonomy?.skinConcern?.map((item) => ({
      label: item.name,
      value: item.name,
    })) || [];

  const handleSortChange = (value: string) => {
    setSort(value);
    onSortChange(value);
  };

  const handleSkinTypeChange = (values: string[]) => {
    setSkinTypes(values);
    onSkinTypeChange(values);
  };

  const handleSkinConcernChange = (values: string[]) => {
    setSkinConcerns(values);
    onSkinConcernChange(values);
  };

  const renderTags = () => (
    <>
      {sort && (
        <Tag key={sort}>
          {sortOptions.find((opt) => opt.value === sort)?.label}
          <TagClose
            aria-label="deselect"
            onClick={() => {
              setSort('');
              onSortChange('');
            }}
            tabIndex={0}
          >
            <CloseIcon />
          </TagClose>
        </Tag>
      )}
      {skinTypeOptions
        .filter((opt) => skinTypes.includes(opt.value))
        .map((opt) => (
          <Tag key={opt.value}>
            {opt.label}
            <TagClose
              aria-label="deselect"
              onClick={() => {
                const newValues = skinTypes.filter((v) => v !== opt.value);
                setSkinTypes(newValues);
                onSkinTypeChange(newValues);
              }}
              tabIndex={0}
            >
              <CloseIcon />
            </TagClose>
          </Tag>
        ))}
      {skinConcernOptions
        .filter((opt) => skinConcerns.includes(opt.value))
        .map((opt) => (
          <Tag key={opt.value}>
            {opt.label}
            <TagClose
              aria-label="deselect"
              onClick={() => {
                const newValues = skinConcerns.filter((v) => v !== opt.value);
                setSkinConcerns(newValues);
                onSkinConcernChange(newValues);
              }}
              tabIndex={0}
            >
              <CloseIcon />
            </TagClose>
          </Tag>
        ))}
    </>
  );

  const renderDropdowns = () => (
    <>
      <Select options={sortOptions} value={sort} onChange={handleSortChange} title="Sort" />
      <MultipleSelect options={skinTypeOptions} values={skinTypes} onChange={handleSkinTypeChange} title="Skin type" />
      <MultipleSelect
        options={skinConcernOptions}
        values={skinConcerns}
        onChange={handleSkinConcernChange}
        title="Skin concern"
      />
    </>
  );

  if (isMobile) {
    // Mobile layout: dropdowns first, then tags below
    return (
      <>
        <Container>{renderDropdowns()}</Container>
        {(sort || skinTypes.length > 0 || skinConcerns.length > 0) && (
          <MobileTagsContainer>{renderTags()}</MobileTagsContainer>
        )}
      </>
    );
  }

  // Desktop layout: dropdowns and tags inline
  return (
    <DesktopContainer>
      <DropdownsContainer>{renderDropdowns()}</DropdownsContainer>
      {(sort || skinTypes.length > 0 || skinConcerns.length > 0) && <TagsContainer>{renderTags()}</TagsContainer>}
    </DesktopContainer>
  );
};

export default ReviewFilterBar;
